import {
  ENQUIRY_FORM_SUBMIT_PERSONAL_FAILED,
  ENQUIRY_FORM_SUBMIT_PERSONAL_SUCCESS,
} from "../../constants/analyticsEvents";
import { sendMailjet, getEnquiryMailPayload, getAcknowledgementMailPayload } from "../../helpers/sendEmail";
import useAnalyticsEventTracker from "../../helpers/useAnalyticsEventTracker";
import Textfield from "../Textfield/Textfield";
import FilledButton from "./FilledButton";
import { useState } from "react";
type Props = {};

// Define types for the form data and errors
interface FormData {
  firstName: string;
  lastName: string;
  email: string;
  message: string;
}

interface FormErrors {
  firstName: string;
  lastName: string;
  email: string;
  message: string;
}
// Validation functions
const validateFirstName = (firstName: string): string => {
  const regex = /^[A-Za-z]{2,}$/;
  return regex.test(firstName)
    ? ""
    : "First name should be at least 2 characters and contain only letters.";
};

const validateLastName = (lastName: string): string => {
  const regex = /^[A-Za-z]{2,}$/;
  return regex.test(lastName)
    ? ""
    : "Last name should be at least 2 characters and contain only letters.";
};

const validateEmail = (email: string): string => {
  const regex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
  return regex.test(email) ? "" : "Please enter a valid email address.";
};

const validateMessage = (message: string): string => {
  return message.trim().length >= 10
    ? ""
    : "Message should be at least 10 characters long.";
};

const Form = (props: Props) => {
  const eventTracker = useAnalyticsEventTracker();

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isSuccess, setIsSuccess] = useState<Record<string, boolean>>({
    success: false,
    showMessage: false,
  });
  const [formData, setFormData] = useState<FormData>({
    firstName: "",
    lastName: "",
    email: "",
    message: "",
  });

  const [errors, setErrors] = useState<FormErrors>({
    firstName: "",
    lastName: "",
    email: "",
    message: "",
  });

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
  };
  const clearForm = () => {
    setFormData({
      firstName: "",
      lastName: "",
      email: "",
      message: "",
    });
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const newErrors: FormErrors = {
      firstName: validateFirstName(formData.firstName),
      lastName: validateLastName(formData.lastName),
      email: validateEmail(formData.email),
      message: validateMessage(formData.message),
    };

    setErrors(newErrors);

    // If no errors, proceed with form submission logic
    if (!Object.values(newErrors).some((error) => error)) {
      const { email, firstName, lastName, message } = formData;
      setIsLoading(true);
      try {
        // Send internal enquiry email
        const enquiryPayload = getEnquiryMailPayload({
          fname: firstName,
          lname: lastName,
          email,
          message,
        });
        let resCode = await sendMailjet(enquiryPayload);

        // Send acknowledgement email to client if first succeeded
        if (resCode < 300) {
          const ackPayload = getAcknowledgementMailPayload(email, firstName, lastName);
          await sendMailjet(ackPayload);
          clearForm();
          setIsSuccess({ success: true, showMessage: true });
          eventTracker(ENQUIRY_FORM_SUBMIT_PERSONAL_SUCCESS);
        } else {
          setIsSuccess({ success: false, showMessage: true });
          eventTracker(ENQUIRY_FORM_SUBMIT_PERSONAL_FAILED);
        }
      } catch (error) {
        setIsSuccess({ success: false, showMessage: true });
        eventTracker(ENQUIRY_FORM_SUBMIT_PERSONAL_FAILED);
      } finally {
        setIsLoading(false);
      }
    }
  };
  return (
    <form onSubmit={handleSubmit} className="mx-auto space-y-8 md:w-3/4">
      <div className="grid gap-8 sm:grid-cols-2 sm:gap-4">
        <Textfield
          label="First Name"
          placeholder="First Name"
          id="tf-fname"
          name="firstName"
          autocomplete="given-name"
          value={formData.firstName}
          onChange={handleChange}
          error={errors.firstName}
        />
        <Textfield
          label="Last Name"
          placeholder="Last Name"
          id="tf-lname"
          name="lastName"
          autocomplete="family-name"
          value={formData.lastName}
          onChange={handleChange}
          error={errors.lastName}
        />
      </div>
      <Textfield
        label="Email"
        placeholder="Email"
        id="tf-email"
        name="email"
        autocomplete="email"
        value={formData.email}
        onChange={handleChange}
        error={errors.email}
      />
      <Textfield
        label="Message"
        placeholder="Message"
        id="tf-message"
        textarea={true}
        name="message"
        value={formData.message}
        onChange={handleChange}
        error={errors.message}
      />
      <FilledButton label="Send Message" isLoading={isLoading} />

      {isSuccess.showMessage ? (
        isSuccess.success ? (
          <div className="text-xs text-green-500 text-right">
            Message Sent Successfully
          </div>
        ) : (
          <div className="text-xs text-red-500 text-right">
            Failed to Send Email
          </div>
        )
      ) : null}
    </form>
  );
};

export default Form;
